import React from 'react';
import { Play, Search } from 'lucide-react';

interface ContractAmbiguityToolbarProps {
  filters: {
    severity: string;
    flagType: string;
    keyword: string;
  };
  onFilterChange: (filters: { severity: string; flagType: string; keyword: string }) => void;
  onRunAnalysis: () => void;
  isRunning: boolean;
  hasFindings: boolean;
}

export const ContractAmbiguityToolbar: React.FC<ContractAmbiguityToolbarProps> = ({
  filters,
  onFilterChange,
  onRunAnalysis,
  isRunning,
  hasFindings,
}) => {
  return (
    <div className="flex flex-wrap items-center gap-3" data-testid="ambiguity-toolbar">
      {/* Run Analysis button */}
      <button
        onClick={onRunAnalysis}
        disabled={isRunning}
        className="inline-flex items-center px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors disabled:bg-primary-300 text-sm font-medium"
        data-testid="run-ambiguity-analysis-button"
      >
        {isRunning ? (
          <>
            <div className="animate-spin h-4 w-4 border-2 border-white border-t-transparent rounded-full mr-2" />
            Running...
          </>
        ) : (
          <>
            <Play className="h-4 w-4 mr-2" />
            Run Analysis
          </>
        )}
      </button>

      {/* Filters — only show when we have findings */}
      {hasFindings && (
        <>
          {/* Severity dropdown */}
          <select
            value={filters.severity}
            onChange={(e) =>
              onFilterChange({ ...filters, severity: e.target.value })
            }
            className="px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-primary-500 focus:border-primary-500"
            data-testid="ambiguity-severity-filter"
          >
            <option value="">All Severities</option>
            <option value="high">High</option>
            <option value="medium">Medium</option>
            <option value="low">Low</option>
          </select>

          {/* Flag type dropdown */}
          <select
            value={filters.flagType}
            onChange={(e) =>
              onFilterChange({ ...filters, flagType: e.target.value })
            }
            className="px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-primary-500 focus:border-primary-500"
            data-testid="ambiguity-flag-type-filter"
          >
            <option value="">All Categories</option>
            <option value="time_related">Time-Related Terms</option>
            <option value="quantity_qualifier">Quantity/Degree Qualifier</option>
            <option value="frequency">Frequency</option>
            <option value="obligation_level">Obligation Level</option>
            <option value="performance_standard">Performance Standard</option>
            <option value="approval_acceptance">Approval & Acceptance</option>
            <option value="condition_word">Condition Word</option>
            <option value="negative_ambiguity">Negative Ambiguity</option>
            <option value="change_modification">Change/Modification</option>
            <option value="time_commitment">Time Commitment</option>
          </select>

          {/* Keyword search */}
          <div className="relative">
            <Search className="h-4 w-4 absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
            <input
              type="text"
              value={filters.keyword}
              onChange={(e) =>
                onFilterChange({ ...filters, keyword: e.target.value })
              }
              placeholder="Search clauses..."
              className="pl-9 pr-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-primary-500 focus:border-primary-500 w-48"
              data-testid="ambiguity-keyword-filter"
            />
          </div>
        </>
      )}
    </div>
  );
};
